import type * as GeoJSON from "geojson";

/**
 * Shallow-compare two per-feature property bags. `featureValuesById` maps are
 * rebuilt by callers on unrelated renders, so identity alone can't tell a real
 * value change from a fresh-but-identical object.
 */
function areFeaturePropsEqual(a: GeoJSON.GeoJsonProperties, b: GeoJSON.GeoJsonProperties): boolean {
  if (a === b) return true;
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) return a === b;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => Object.hasOwn(b, key) && Object.is(a[key], b[key]));
}

/**
 * Diff the incoming per-feature values against the last applied snapshot.
 * Returns only entries that are new or changed, so identical-content maps
 * (rebuilt by callers on unrelated renders) produce no source update — and no
 * choropleth repaint — at all.
 */
export function diffFeatureValues(
  prev: ReadonlyMap<string, GeoJSON.GeoJsonProperties | null> | null,
  next: ReadonlyMap<string, GeoJSON.GeoJsonProperties | null>,
): { id: string; props: NonNullable<GeoJSON.GeoJsonProperties> }[] {
  const changed: { id: string; props: NonNullable<GeoJSON.GeoJsonProperties> }[] = [];
  for (const [featureId, props] of next) {
    if (props === null || props === undefined) continue;
    if (prev) {
      const old = prev.get(featureId);
      if (old !== null && old !== undefined && areFeaturePropsEqual(old, props)) continue;
    }
    changed.push({ id: featureId, props });
  }
  return changed;
}
